<template>
  <v-dialog :value="value" max-width="760px" persistent scrollable @input="$emit('input', $event)">
    <v-card>

      <!-- card header -->
      <v-card-title>
        <span class="headline">{{ editMode ? "تعديل نوع منتج" : "إضافة نوع منتج" }}</span>
      </v-card-title>

      <!-- card body -->
      <v-card-text>
        <v-progress-linear v-if="fetching" indeterminate color="primary" class="mb-4" rounded />

        <!-- ref="form" mean that we can call form function using this.$refs.form. -->
         <!-- v-model="valid" means that the form is valid when all validation rules pass. -->
        <v-form ref="form" v-model="valid">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.name"
                label="اسم المنتج"
                :rules="[rules.required]"
                outlined
                dense
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                v-model="form.value"
                label="القيمة البرمجية (value)"
                hint="مثال: commercial_finance"
                persistent-hint
                :rules="[rules.required, rules.valuePattern]"
                outlined
                dense
                dir="ltr"
              />
            </v-col>
            <v-col cols="12">
              <v-textarea
                v-model="form.description"
                label="الوصف (عربي)"
                rows="3"
                auto-grow
                outlined
                dense
              />
            </v-col>

            <v-col cols="12" md="6">
              <v-file-input
                v-model="imageFile"
                label="صورة المنتج"
                accept="image/*"
                prepend-icon="mdi-camera"
                outlined
                dense
                show-size
                truncate-length="24"
                @change="onImageSelected"
              />
              <div v-if="imagePreview" class="image-preview-wrap mt-2">
                <v-img :src="imagePreview" max-height="120" max-width="160" contain class="product-image-preview" />
                <v-btn x-small text color="error" class="mt-1" @click="clearImageSelection">
                  إزالة الصورة المحددة
                </v-btn>
              </div>
            </v-col>

            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="form.sort"
                label="الترتيب (sort)"
                type="number"
                outlined
                dense
              />
            </v-col>
            <!--
            <v-col cols="12" sm="6">
              <v-text-field
                v-model.number="form.sort_by"
                label="الترتيب (sort_by)"
                type="number"
                outlined
                dense
              />
            </v-col>
            -->

            <v-col cols="12" sm="6">
              <v-switch v-model="form.active" inset label="نشط" color="primary" hide-details />
            </v-col>
            <v-col cols="12" sm="6">
              <v-switch v-model="form.site_show" inset label="يظهر في الموقع" color="primary" hide-details />
            </v-col>

            <v-col cols="12">
              <div class="field-label mb-2">الاستشاريون الداخليون</div>
              <v-autocomplete
                v-model="internalAgents"
                :items="agentOptions"
                :loading="loadingAgents"
                item-text="name"
                item-value="id"
                label="اختر الاستشاريين الداخليين"
                outlined
                dense
                multiple
                chips
                deletable-chips
                return-object
                hide-details
                clearable
              />
            </v-col>

            <v-col cols="12">
              <div class="field-label mb-2">الاستشاريون الخارجيون</div>
              <v-autocomplete
                v-model="externalAgents"
                :items="agentOptions"
                :loading="loadingAgents"
                item-text="name"
                item-value="id"
                label="اختر الاستشاريين الخارجيين"
                outlined
                dense
                multiple
                chips
                deletable-chips
                return-object
                hide-details
                clearable
              />
            </v-col>
          </v-row>
        </v-form>
      </v-card-text>

      <!-- card footer -->
      <v-card-actions>
        <!-- Pushes buttons to the right in the footer. -->
        <v-spacer />
        <v-btn text @click="close">إلغاء</v-btn>
        <v-btn color="primary" :loading="saving" :disabled="fetching" @click="submit">
          {{ editMode ? "حفظ التعديلات" : "إضافة" }}
        </v-btn>
      </v-card-actions>

    </v-card>
  </v-dialog>
</template>

<script>
import Axios from "axios";

const emptyForm = () => ({
  name: "",
  value: "",
  description: "",
  active: true,
  site_show: true,
  sort: null,
  // sort_by: null,
});

export default {
  name: "ProductTypeForm",
  // props are the properties that are passed to the component from the parent component.
  props: {
    value: { type: Boolean, default: false },
    editId: { type: [Number, String], default: null },
  },
  data() {
    return {
      valid: false,
      fetching: false,
      saving: false,
      loadingAgents: false,
      form: emptyForm(),
      agentOptions: [],
      internalAgents: [],
      externalAgents: [],
      imageFile: null,
      imagePreview: "",
      existingImageUrl: "",
      rules: {
        required: (v) => !!String(v || "").trim() || "مطلوب",
        valuePattern: (v) =>
          /^[a-z0-9_]+$/.test(String(v || "").trim()) ||
          "حروف إنجليزية صغيرة وأرقام و _ فقط",
      },
    };
  },
  computed: {
    editMode() {//true if editId is set, false otherwise
      return this.editId != null && this.editId !== "";
    },
    apiBase() {//the base URL for the API
      return `${this.$store.state.url}/employee/admin/product-types`;
    },
  },
  //Runs code when something changes:
  watch: {
    value(open) {//runs when the dialog is opened or closed
      if (open) {
        this.resetForm();
        this.loadAgentOptions();
        if (this.editMode) {
          this.loadItem();
        }
      }
    },
  },
  methods: {
    resetForm() {
      this.form = emptyForm();
      this.internalAgents = [];
      this.externalAgents = [];
      this.imageFile = null;
      this.imagePreview = "";
      this.existingImageUrl = "";
      this.$nextTick(() => {
        if (this.$refs.form) this.$refs.form.resetValidation();
      });
    },
    close() {
      this.$emit("input", false);
    },
    async loadAgentOptions() {
      this.loadingAgents = true;
      try {
        const res = await Axios.get(`${this.apiBase}/meta/agents`, {
          headers: this.request_headers(),
        });
        this.agentOptions = res.data.payload || [];
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "تعذر تحميل قائمة الاستشاريين");
      } finally {
        this.loadingAgents = false;
      }
    },
    //Maps API agents to { id, name } for autocomplete
    mapAgentSelection(list) {
      return (list || []).map((agent) => ({
        id: agent.id,
        name: agent.name,
      }));
    },
    async loadItem() {//loads the item data from the API for editing
      this.fetching = true;
      try {
        const res = await Axios.get(`${this.apiBase}/${this.editId}`, {
          headers: this.request_headers(),
        });
        const item = res.data.payload || {};
        this.form = {
          name: item.name_ar || item.name || "",
          value: item.value || "",
          description: item.description_ar || item.description || "",
          active: item.active !== false,
          site_show: item.site_show !== false,
          sort: item.sort,
          // sort_by: item.sort_by,
        };
        this.internalAgents = this.mapAgentSelection(item.internal_agents);
        this.externalAgents = this.mapAgentSelection(item.external_agents);
        this.existingImageUrl = item.image_url || "";
        this.imagePreview = this.existingImageUrl;
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "تعذر تحميل البيانات");
        this.close();
      } finally {
        this.fetching = false;
      }
    },
    onImageSelected() {//Creates preview URL from selected file
      const file = Array.isArray(this.imageFile) ? this.imageFile[0] : this.imageFile;
      if (file) {
        this.imagePreview = URL.createObjectURL(file);
        return;
      }
      this.imagePreview = this.existingImageUrl || "";
    },
    clearImageSelection() {//Clears new file; keeps existing image preview
      this.imageFile = null;
      this.imagePreview = this.existingImageUrl || "";
    },
    buildFormData() {
      const fd = new FormData();
      fd.append("name", this.form.name.trim());
      fd.append("value", this.form.value.trim().toLowerCase());
      fd.append("description", this.form.description || "");
      fd.append("active", this.form.active ? 1 : 0);
      fd.append("site_show", this.form.site_show ? 1 : 0);

      if (this.form.sort === "" || this.form.sort == null) {
        fd.append("sort", 0);
      } else {
        fd.append("sort", Number(this.form.sort));
      }

      // if (this.form.sort_by === "" || this.form.sort_by == null) {
      //   fd.append("sort_by", "");
      // } else {
      //   fd.append("sort_by", Number(this.form.sort_by));
      // }

      this.internalAgents.forEach((agent, index) => {
        fd.append(`internal_agent_ids[${index}]`, agent.id);
      });
      this.externalAgents.forEach((agent, index) => {
        fd.append(`external_agent_ids[${index}]`, agent.id);
      });

      const file = Array.isArray(this.imageFile) ? this.imageFile[0] : this.imageFile;//if the imageFile is an array, use the first element, otherwise use the imageFile
      if (file) {
        fd.append("image", file);
      }

      return fd;
    },
    async submit() {//submits the form data to the API
      if (!this.$refs.form.validate()) return;//if the form is not valid, return

      this.saving = true;
      try {
        const fd = this.buildFormData();
        const headers = this.request_headers();

        if (this.editMode) {//if we are editing, send a PUT request to the API
          await Axios.post(`${this.apiBase}/${this.editId}`, fd, { headers });
          this.showPopup("success", "تم تحديث نوع المنتج");
        } else {//if we are adding, send a POST request to the API
          await Axios.post(this.apiBase, fd, { headers });
          this.showPopup("success", "تم إضافة نوع المنتج");
        }
        /*
        Emit = “this child tells the parent something happened.”

        this.$emit('input', false);   // close dialog (works with v-model)
        this.$emit('saved');          // parent runs fetchItems()
        */
        this.$emit("saved");//emits the saved event to the parent component
        this.close();//closes the dialog
      } catch (e) {
        this.showPopup("error", e.response?.data?.message || "تعذر الحفظ");
      } finally {//finally block runs after the try or catch block, regardless of whether an error occurred
        this.saving = false;
      }
    },
  },
};
</script>

<!-- scoped means that the styles are only applied to this component -->
<style scoped>
.field-label {
  font-weight: 600;
  font-size: 0.9rem;
}

.image-preview-wrap {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.product-image-preview {
  border-radius: 8px;
  border: 1px solid rgba(140, 140, 140, 0.35);
  background: rgba(0, 0, 0, 0.03);
}

.theme--dark .product-image-preview,
.dark .product-image-preview {
  border-color: rgba(255, 255, 255, 0.18);
  background: rgba(255, 255, 255, 0.04);
}

.theme--dark .field-label,
.dark .field-label {
  color: rgba(255, 255, 255, 0.87);
}
</style>
